import { useEffect, useMemo, useRef, useState } from "react";
import {
  getRevisionConvocatoriaByProject,
  getRevisionJornadaCatalogNames,
} from "./revisionRapidaSaludApi";
import { getRevisionJornadaContext } from "./revisionRapidaSaludExcel";
import {
  JORNADA_CONTEXT_PENDING_VALUE,
  getSummaryConvocatoriaContext,
  getValidConvocatoriaContext,
  toResolvedJornadaValue,
} from "./revisionRapidaSaludJornadaContext";

const pendingContext = Object.freeze({
  estado: JORNADA_CONTEXT_PENDING_VALUE,
  municipio: JORNADA_CONTEXT_PENDING_VALUE,
  parroquia: JORNADA_CONTEXT_PENDING_VALUE,
  establecimiento: JORNADA_CONTEXT_PENDING_VALUE,
  fechaAtencion: JORNADA_CONTEXT_PENDING_VALUE,
  source: "pending",
});

const hasCompleteSummaryContext = (context) =>
  [
    context.estado,
    context.municipio,
    context.parroquia,
    context.establecimiento,
    context.fechaAtencion,
  ].every((value) => value && value !== JORNADA_CONTEXT_PENDING_VALUE && value !== "Sin dato");

const useJornadaLocationContext = ({
  convocatoriaId,
  navigationContext,
  summary,
  summaryResolved,
  detail,
}) => {
  const recoverySequenceRef = useRef(0);
  const catalogSequenceRef = useRef(0);
  const navigationConvocatoria = useMemo(
    () => getValidConvocatoriaContext(navigationContext, convocatoriaId),
    [convocatoriaId, navigationContext]
  );
  const summaryConvocatoria = useMemo(
    () => getSummaryConvocatoriaContext(summary, convocatoriaId),
    [convocatoriaId, summary]
  );
  const summaryJornadaContext = useMemo(() => {
    const context = getRevisionJornadaContext({ summary });
    return hasCompleteSummaryContext(context) ? context : null;
  }, [summary]);
  const [recoveredConvocatoria, setRecoveredConvocatoria] = useState(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [catalogState, setCatalogState] = useState({
    loading: false,
    estadoNombre: "",
    municipioNombre: "",
    parroquiaNombre: "",
  });

  useEffect(() => {
    const sequence = ++recoverySequenceRef.current;
    setRecoveredConvocatoria(null);

    if (navigationConvocatoria || summaryConvocatoria || summaryJornadaContext || !summaryResolved) {
      setRecoveryLoading(false);
      return undefined;
    }

    const projectId = summary?.convocatoria?.projectId ?? summary?.convocatoria?.idProyecto;
    if (projectId === null || projectId === undefined || projectId === "") {
      setRecoveryLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setRecoveryLoading(true);
    Promise.resolve(getRevisionConvocatoriaByProject(convocatoriaId, projectId, {
      signal: controller.signal,
    }))
      .then((source) => {
        if (sequence !== recoverySequenceRef.current || !source) return;
        setRecoveredConvocatoria(getValidConvocatoriaContext(source, convocatoriaId));
      })
      .catch(() => {
        if (sequence === recoverySequenceRef.current) setRecoveredConvocatoria(null);
      })
      .finally(() => {
        if (sequence === recoverySequenceRef.current) setRecoveryLoading(false);
      });

    return () => {
      controller.abort();
      if (sequence === recoverySequenceRef.current) recoverySequenceRef.current += 1;
    };
  }, [
    convocatoriaId,
    navigationConvocatoria,
    summary,
    summaryConvocatoria,
    summaryJornadaContext,
    summaryResolved,
  ]);

  const convocatoriaContext = navigationConvocatoria || summaryConvocatoria || recoveredConvocatoria;

  useEffect(() => {
    const sequence = ++catalogSequenceRef.current;
    if (!convocatoriaContext) {
      setCatalogState({ loading: false, estadoNombre: "", municipioNombre: "", parroquiaNombre: "" });
      return undefined;
    }

    const controller = new AbortController();
    setCatalogState({ loading: true, estadoNombre: "", municipioNombre: "", parroquiaNombre: "" });
    Promise.resolve(getRevisionJornadaCatalogNames(convocatoriaContext, {
      signal: controller.signal,
    }))
      .then((names) => {
        if (sequence !== catalogSequenceRef.current) return;
        setCatalogState({
          loading: false,
          estadoNombre: names?.estadoNombre || "",
          municipioNombre: names?.municipioNombre || "",
          parroquiaNombre: names?.parroquiaNombre || "",
        });
      })
      .catch(() => {
        if (sequence !== catalogSequenceRef.current) return;
        setCatalogState({ loading: false, estadoNombre: "", municipioNombre: "", parroquiaNombre: "" });
      });

    return () => {
      controller.abort();
      if (sequence === catalogSequenceRef.current) catalogSequenceRef.current += 1;
    };
  }, [convocatoriaContext]);

  if (convocatoriaContext) {
    const territorialValue = (value) =>
      catalogState.loading ? JORNADA_CONTEXT_PENDING_VALUE : toResolvedJornadaValue(value);

    return {
      jornadaContext: {
        estado: territorialValue(catalogState.estadoNombre),
        municipio: territorialValue(catalogState.municipioNombre),
        parroquia: territorialValue(catalogState.parroquiaNombre),
        establecimiento: toResolvedJornadaValue(convocatoriaContext.comunidad),
        fechaAtencion: toResolvedJornadaValue(convocatoriaContext.fecha),
        source: navigationConvocatoria ? "navigation" : summaryConvocatoria ? "summary" : "server",
      },
      convocatoriaContext,
    };
  }

  if (summaryJornadaContext) {
    return { jornadaContext: summaryJornadaContext, convocatoriaContext: null };
  }

  if (!summaryResolved || recoveryLoading) {
    return { jornadaContext: pendingContext, convocatoriaContext: null };
  }

  return {
    jornadaContext: getRevisionJornadaContext({ detail }),
    convocatoriaContext: null,
  };
};

export default useJornadaLocationContext;
